import * as R from 'ramda'
import { capitalize } from 'lodash-es'
import { format, isBefore } from 'date-fns'

import { formatDayAndHour } from '../../libs/date'
import {
  INCIDENT_CODE_INCORRECT_ADDRESS,
  SHIPPING_STATUS_CODES,
  SHIPPING_STATUS_CODES_NO_WEB,
  SHIPPING_WITH_ERE,
  SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
  SHIPPING_WITH_REFUND_CODE,
  SHIPPING_WITH_RETURN,
  SHIPPING_WITH_SLIP_SCAN,
} from '../../common/constants'
import { SHIPPING_STATES } from './constants'
import { getColorChipForTrackingState } from '../../context/businessConstants'

// El endpoint unificado informa `shipping_status_code`/`current_status_code`
// sin el prefijo 'WEB' (ver SHIPPING_STATUS_CODES_NO_WEB); esta tabla traduce
// esos códigos crudos al código canónico con prefijo que usan `statusList`
// (catálogo de estados del usuario) y el resto de constantes/consumidores.
// Algunas entradas (p.ej. `stored`/`deliveringFilter`) son en realidad varios
// códigos individuales unidos por comas (p.ej. '1700,1800,1900'), así que se
// expanden a pares 1:1 con su equivalente WEB: un código crudo real como
// '1700' nunca llegará como '1700,1800,1900' y por tanto nunca casaría contra
// una clave sin expandir.
const NO_WEB_TO_WEB_STATUS_CODE = Object.fromEntries(
  Object.entries(SHIPPING_STATUS_CODES_NO_WEB).flatMap(([key, code]) => {
    const noWebCodes = code.split(',')
    const webCodes = SHIPPING_STATUS_CODES[key].split(',')
    return noWebCodes.map((noWebCode, i) => [noWebCode, webCodes[i] ?? webCodes[0]])
  }),
)
const toWebStatusCode = code => NO_WEB_TO_WEB_STATUS_CODE[code] ?? code

// SHIPPING_STATUS_CODES_NO_WEB es SHIPPING_STATUS_CODES sin el prefijo 'WEB'
// (ver constants.js): para construir filtros hacia el endpoint unificado
// basta con quitarlo del código, sin necesitar una tabla de traducción.
const toNoWebStatusCode = code => code?.replace(/WEB/g, '') ?? code

const findAdditional = (additionals, additionalCode) =>
  additionals.find(({ additional_code }) => additional_code === additionalCode)

// El endpoint unificado devuelve `shipping.additionals` con las claves en
// camelCase (additionalCode/additionalFlag/...), pero el resto de este
// fichero (y otros consumidores como useShippingActions.js) da por hecho el
// snake_case heredado del endpoint viejo (additional_code/additional_flag).
// Se normaliza aquí, una única vez, dejando ambas variantes disponibles para
// no romper a ningún consumidor existente.
const normalizeAdditional = additional => ({
  ...additional,
  additional_code: additional.additional_code ?? additional.additionalCode,
  additionalCode: additional.additionalCode ?? additional.additional_code,
  additional_flag: additional.additional_flag ?? additional.additionalFlag,
  additionalFlag: additional.additionalFlag ?? additional.additional_flag,
  additional_sub_code:
    additional.additional_sub_code ?? additional.additionalSubCode,
  additionalSubCode:
    additional.additionalSubCode ?? additional.additional_sub_code,
  additional_value: additional.additional_value ?? additional.additionalValue,
  additionalValue: additional.additionalValue ?? additional.additional_value,
  additional_text: additional.additional_text ?? additional.additionalText,
  additionalText: additional.additionalText ?? additional.additional_text,
  point_name: additional.point_name ?? additional.pointName,
  pointName: additional.pointName ?? additional.point_name,
})

const getShippingState = shippingStateCode => {
  return SHIPPING_STATES[shippingStateCode]
}

// Nuevo formato de filtros para POST /item-history-api/private/v2/rpc-shippings.
// Sustituye a la vieja transformFiltersToBody (POST /rpc-shippings, endpoint
// retirado; eliminada junto con el resto de código muerto de esta migración).
// El backend solo ha documentado un único ejemplo de request/response; los mapeos
// marcados con "TODO: validar contra test" son la mejor inferencia posible a partir
// de ese ejemplo y deben confirmarse contra api-test.cttexpress.com antes de darlos
// por definitivos. `allowCancelled` se mantiene sin uso por paridad con la firma
// original de transformFiltersToBody (ya era un parámetro sin uso ahí). `noCount`
// tampoco tiene un equivalente visible en el nuevo formato (no hay ejemplo de un
// flag para omitir el recuento total) — TODO: confirmar con backend.
// Los dos identificadores de centro que llegan en los datos iniciales:
//   centre.id     = client_center_code -> el VISIBLE (p.ej. '2525025'); es el
//                   que se pinta al usuario en el selector de centro.
//   centre.hideId = client_center_id   -> el OFUSCADO (cifrado en base64,
//                   p.ej. 'IxY4gC0ktDuiS0xlhJvQPg=='); es el que se guarda en
//                   los filtros de UI/URL (useQueryParamsFilters) y el que
//                   espera este endpoint en `client_center_code`.
// Este mapa traduce id -> hideId para cubrir el caso en que el filtro llegue
// con el código visible, a partir de la lista completa de centros del usuario
// (useUserContext().centres), que hay que pasar explícitamente a
// transformFiltersToRpcShippingsBody.
const buildCentreCodeToHideIdMap = centres =>
  new Map((centres ?? []).map(centre => [String(centre.id), centre.hideId]))

const transformFiltersToRpcShippingsBody = (filters, allowCancelled, noCount, locale, centres, isMaster) => {
  const rpcFilters = []
  const centreCodeToHideId = buildCentreCodeToHideIdMap(centres)
  // Si el código no está en el mapa se asume que ya venía ofuscado (el caso
  // habitual: hideId desde useQueryParamsFilters/la URL o el selector).
  const toObfuscatedCentreCode = code =>
    centreCodeToHideId.get(String(code)) ?? code

  const addFilter = (field, operation, valueProps) =>
    rpcFilters.push({ field, operation, ...valueProps })


  addFilter('item_number', 'EQUALS', { value: 1 })

  addFilter('current_status_code', 'IS_NOT_NULL', {})

  if (!R.isNil(filters?.clientCode)) {
    addFilter('client_code', 'EQUALS', { value: filters.clientCode })
  }

  if (!R.isNil(filters?.clientCenterCode)) {
    // Selección explícita del filtro avanzado (FiltersDrawer/ClientCenterCode):
    // se respeta siempre, incluso para usuarios master.
    addFilter('client_center_code', 'IN', {
      value: filters.clientCenterCode
        .split(',')
        .map(toObfuscatedCentreCode),
    })
  } else if (!isMaster && !R.isNil(filters?.centres)) {
    // filters.centres es el valor implícito (todos los centros del usuario,
    // autoseleccionado en useQueryParamsFilters). Los usuarios master ven
    // envíos de todos los centros del cliente por defecto, así que este
    // filtro implícito no debe aplicarse para ellos.
    if (Array.isArray(filters.centres)) {
      // TODO: validar contra test si 'client_center_code' admite IN con un
      // array de códigos o si solo acepta EQUALS con un valor único.
      addFilter('client_center_code', 'IN', {
        value: filters.centres.map(centre => centre.hideId),
      })
    } else {
      // filters.centres llega como string de códigos ofuscados
      // (comma-joined hideId) desde useQueryParamsFilters/la URL, que es ya el
      // formato que espera el endpoint.
      addFilter('client_center_code', 'IN', {
        value: filters.centres.split(',').map(toObfuscatedCentreCode),
      })
    }
  }

  if (!R.isNil(filters?.serviceType)) {
    // TODO: validar contra test nombre de campo/operación (asumidos iguales al body viejo).
    addFilter('shipping_type_code', 'EQUALS', {
      value: filters.serviceType.split('-')[0],
    })
  }

  if (!R.isNil(filters?.postalCodeFrom)) {
    // TODO: validar contra test - nombre de campo inferido del único ejemplo dado
    // ('destin_contact_info.postal_code'); no confirmado si 'origin' sigue el
    // mismo patrón ('origin_contact_info...') o debe ser 'sender_contact_info...',
    // ni si RANGE es válido para un código postal (string, no numérico/fecha).
    const field = `${filters.postalCodeSource}_contact_info.postal_code`
    if (!R.isNil(filters?.postalCodeTo)) {
      addFilter(field, 'RANGE', {
        from_value: filters.postalCodeFrom,
        to_value: filters.postalCodeTo,
      })
    } else {
      addFilter(field, 'STARTS_WITH', { value: filters.postalCodeFrom })
    }
  }

  if (!R.isNil(filters?.originPostalCode)) {
    // TODO: validar contra test - nombre de campo no confirmado (ver nota anterior).
    addFilter('origin_contact_info.postal_code', 'STARTS_WITH', {
      value: filters.originPostalCode,
    })
  }

  if (!R.isNil(filters?.destinPostalCode)) {
    addFilter('destin_contact_info.postal_code', 'STARTS_WITH', {
      value: filters.destinPostalCode,
    })
  }

  if (!R.isNil(filters?.country)) {
    // TODO: validar contra test - nombre de campo no confirmado, no hay ejemplo de país.
    addFilter('destin_contact_info.country_code', 'EQUALS', {
      value: filters.country,
    })
  }

  if (!R.isNil(filters?.dateFrom) && R.isNil(filters?.search)) {
    // TODO: validar contra test - el body viejo filtraba por 'shipping_date';
    // el ejemplo del nuevo endpoint usa 'manifest_datetime'. Confirmar equivalencia
    // semántica antes de dar esto por bueno.
    const from = `${filters.dateFrom}T00:00:00Z`
    const to = !R.isNil(filters?.dateTo)
      ? `${filters.dateTo}T23:59:59Z`
      : `${filters.dateFrom}T23:59:59Z`
    addFilter('manifest_datetime', 'RANGE', { from_value: from, to_value: to })
  }

  if (!R.isNil(filters?.statusCode)) {
    const nonCancelledCodes = filters.statusCode.filter(
      code => code !== SHIPPING_STATUS_CODES.cancelled,
    )
    if (nonCancelledCodes.length) {
      addFilter('current_status_code', 'IN', {
        // Algunos valores de SHIPPING_STATUS_CODES son varios códigos unidos
        // por comas (p.ej. 'stored': 'WEB1700,WEB1800,WEB1900'); hay que
        // enviarlos como elementos independientes del array, no como un único
        // string con comas.
        value: nonCancelledCodes.flatMap(code =>
          toNoWebStatusCode(code).split(','),
        ),
      })
    }
    if (filters.statusCode.includes(SHIPPING_STATUS_CODES.cancelled)) {
      // TODO CRÍTICO: validar contra test - el body viejo usaba is_nulled='true'.
      // No sabemos si el nuevo endpoint sigue exponiendo un equivalente a
      // 'is_nulled' o si "cancelado" se representa solo vía current_status_code.
      addFilter('current_status_code', 'EQUALS', {
        value: SHIPPING_STATUS_CODES.cancelled,
      })
    }
  }

  if (!R.isNil(filters?.hasIncident)) {
    if (filters?.incidentCode?.length) {
      const statusArray = []
      const incidentCodes = []

      filters.incidentCode.forEach(item => {
        if (item.includes('&')) {
          const parts = item.split('&')
          incidentCodes.push(parts[0])
          statusArray.push(parts[1])
        } else {
          incidentCodes.push(item)
        }
      })

      if (statusArray.length) {
        addFilter('current_status_code', 'IN', { value: statusArray })
      }
      addFilter('last_incident_type_code', 'IN', { value: incidentCodes })
      if (incidentCodes.includes(INCIDENT_CODE_INCORRECT_ADDRESS)) {
        addFilter('current_status_code', 'EQUALS', {
          value: SHIPPING_STATUS_CODES_NO_WEB.delivering,
        })
      }
    } else if (filters.hasIncident) {
      addFilter('last_incident_type_code', 'IS_NOT_NULL', {})
      addFilter('current_status_code', 'IN', {
        // storedIncident agrupa varios códigos unidos por comas ('1700,1800'):
        // hay que enviarlos como elementos independientes del array.
        value: [
          SHIPPING_STATUS_CODES_NO_WEB.incidentDelivering,
          SHIPPING_STATUS_CODES_NO_WEB.failedCollected,
          SHIPPING_STATUS_CODES_NO_WEB.storedIncident,
        ].flatMap(code => code.split(',')),
      })
    }
  }

  const sortFields = []
  if (!R.isNil(filters?.sortColumn)) {
    // TODO: validar contra test los nombres de campo ordenables (inferidos del
    // body viejo). El ejemplo del backend ordena por defecto por
    // 'last_event_datetime', que no tiene equivalente en este mapa — revisar si
    // conviene añadirlo como columna ordenable nueva.
    const sortField =
      {
        shippingDate: 'manifest_datetime',
        shippingStatusCode: 'current_status_code',
        clientCenterCode: 'client_center_code',
        senderAddress2: 'sender_address_2',
        clientReference: 'client_reference',
        destinAddress2: 'recipient_address_2',
        recipientName: 'recipient_name',
      }[filters.sortColumn] || 'shipping_code'
    sortFields.push({ field: sortField, ascending: filters?.sortType === 'asc' })
  }

  if (!R.isNil(filters?.shippingReference)) {
    addFilter('client_reference', 'EQUALS', { value: filters.shippingReference })
  }

  if (!R.isNil(filters?.senderAddress2)) {
    // TODO: validar contra test - nombre de campo y operación CONTAINS no confirmados.
    addFilter('sender_contact_info.address2', 'CONTAINS', {
      value: filters.senderAddress2,
    })
  }

  if (!R.isNil(filters?.recipientAddress2)) {
    // TODO: validar contra test - nombre de campo y operación CONTAINS no confirmados.
    addFilter('recipient_contact_info.address2', 'CONTAINS', {
      value: filters.recipientAddress2,
    })
  }

  if (!R.isNil(filters?.isUnscheduledPickupSearch)) {
    // Reproduce server-side la query DSL vieja ('previous_shipping_code eq
    // null and is_fixed_pickup eq null'), ya que no podemos confirmar que el
    // backend siga exponiendo 'is_unscheduled' en cada item de la respuesta.
    addFilter('previous_shipping_code', 'IS_NULL', {})
    addFilter('pickup', 'IS_NULL', {})
    addFilter('related_services', 'IS_NULL', {})
  } else if (!R.isNil(filters?.search)) {
    const shippingCodeSearch =
      filters.search.length === 25 ? filters.search.slice(0, -3) : filters.search

    rpcFilters.push({
      group_operation: 'OR',
      field: 'shipping_code',
      filters: [
        {
          field: 'search_values',
          operation: 'EQUALS',
          value: shippingCodeSearch,
        },
        {
          field: 'client_reference',
          operation: 'STARTS_WITH',
          value: filters.search,
        },
        {
          field: 'origin_contact_info.town_name',
          operation: 'STARTS_WITH',
          value: filters.search,
        },
        {
          field: 'destin_contact_info.town_name',
          operation: 'STARTS_WITH',
          value: filters.search,
        },
        {
          field: 'destin_contact_info.name',
          operation: 'STARTS_WITH',
          value: filters.search,
        },
        {
          field: 'prime_shipping_code',
          operation: 'EQUALS',
          value: filters.search,
        },
      ],
    })
  } else {
    if (!R.isNil(filters?.shippingCode)) {
      addFilter('search_values', 'EQUALS', { value: filters.shippingCode })
    }
    if (!R.isNil(filters?.clientReference)) {
      addFilter('client_reference', 'CONTAINS', {
        value: filters.clientReference,
      })
    }
  }

  return {
    page: {
      // TODO: validar contra test si page_offset es 0-indexado (el body viejo
      // sumaba +1 porque el endpoint antiguo era 1-indexado).
      page_offset: Number(filters?.page ?? 0),
      page_limit: Number(filters?.rowsPerPage ?? 25),
    },
    config: {
      view: 'WEBCLIENT',
      language: (locale ?? 'es').toUpperCase(),
      show_items: false,
    },
    filter: {
      group_operation: 'AND',
      filters: rpcFilters,
    },
    ...(sortFields.length ? { sort_fields: sortFields } : {}),
  }
}

const transformFiltersToBodyFile = filters => {
  const body = {}

  if (!R.isNil(filters?.clientCenterCode)) {
    body.client_center_code = filters.clientCenterCode
  } else if (!R.isNil(filters?.centres)) {
    if (Array.isArray(filters.centres)) {
      body.client_center_code = filters.centres
        .map(centre => centre.hideId)
        .join(',')
    } else {
      body.client_center_code = filters.centres
    }
  }

  if (!R.isNil(filters?.country)) {
    body.country_code = filters.country
  }

  if (!R.isNil(filters?.originPostalCode)) {
    body.origin_postal_code_start = filters.originPostalCode
  }

  if (!R.isNil(filters?.destinPostalCode)) {
    body.destin_postal_code_start = filters.destinPostalCode
  }

  if (!R.isNil(filters?.dateFrom)) {
    body.date_from = filters.dateFrom || null
  }

  if (!R.isNil(filters?.dateTo)) {
    body.date_to = filters.dateTo || null
  }

  if (!R.isNil(filters?.statusCode) && filters.statusCode.length) {
    body.shipping_status_codes = filters.statusCode
      .filter(code => code !== SHIPPING_STATUS_CODES.cancelled)
      .join(',')

    if (filters.statusCode.includes(SHIPPING_STATUS_CODES.cancelled)) {
      if (filters.statusCode.length > 0) {
        body.is_nulled = 'true'
      }
    } else if (
      R.isNil(filters?.isUnscheduledPickupSearch) &&
      !filters.search?.length
    ) {
      body.query = 'previous_shipping_code ne null or is_fixed_pickup ne null'
      body.is_nulled = 'false'
    }
  }

  if (!R.isNil(filters?.hasIncident)) {
    if (filters.incidentCode?.length) {
      const statusArray = []
      const incidentCodes = []

      filters.incidentCode.forEach(item => {
        if (item.includes('&')) {
          const parts = item.split('&')
          incidentCodes.push(parts[0])
          statusArray.push(parts[1])
        } else {
          incidentCodes.push(item)
        }
      })

      if (statusArray.length) {
        body.shipping_status_codes = statusArray.join(',')
      }
      body.incident_type_code = incidentCodes.join(',')
    } else {
      body.incident_type_code = filters.hasIncident ? '[not][empty]' : '[null]'
    }
  }

  body.shipping_status_mapping_from = 'WEBCLIENT'
  body.view_type = 'WEB_SHIPPING_TRACKING'

  return body
}

// Adapta el objeto `tracking_info` crudo (snake_case) de la respuesta del
// endpoint unificado a camelCase. Devuelve undefined cuando el envío no trae
// tracking_info (caso de GET /shippings/{id}, endpoint sin migrar todavía).
const parseTrackingInfo = info => {
  const trackingInfo = info?.tracking_info ?? info?.trackingInfo
  if (!trackingInfo) return undefined
  const parseCoordinate = value =>
    value && value !== 'null' ? parseFloat(value) : null
  return {
    milestone: Number(trackingInfo.milestone),
    text: trackingInfo.text,
    isIncident: trackingInfo.is_incident ?? info?.current_status_code === SHIPPING_STATUS_CODES_NO_WEB.incidentDelivering,
    incidentDescription: trackingInfo.incident_description ?? null,
    eventDate: trackingInfo.event_date,
    latitude: parseCoordinate(trackingInfo.latitude),
    longitude: parseCoordinate(trackingInfo.longitude),
    isResolved: Boolean(trackingInfo.is_resolved),
    canResolve: Boolean(trackingInfo.can_resolve),
  }
}

// `shipping_status_code`/`current_status_code` (usados para resolver
// `currentStatus` vía `statusList`) pueden quedar desactualizados/no reflejar
// el último evento real: un envío puede traer '1600' (incidencia en reparto)
// en esos campos mientras que `events` ya incluye un WEB1700 ("Almacenado
// temporalmente") posterior, sin que esos campos se hayan refrescado. Para
// detectar el estado "almacenado" de forma fiable (igual que hace la propia
// TrackingLine, que se basa en `events`) se usa el código del último evento
// real en vez de esos campos.
const getLastEventCode = events =>
  [...(events ?? [])]
    .sort((a, b) =>
      isBefore(new Date(a.event_date), new Date(b.event_date)) ? -1 : 1,
    )
    .slice(-1)[0]?.code

const parseShipping = (shipping, statusList) => {
  const corruptedStatus = {
    code: null,
    reason: '-',
    name: '-',
    canCancel: false,
    canPrint: false,
  }

  const getStatus = currentStatus =>
    statusList?.find(status => status.code === currentStatus) ?? corruptedStatus

  // trackingInfo/isUnscheduled solo están presentes cuando `shipping` viene del
  // endpoint unificado (POST .../item-history-api/private/v2/rpc-shippings).
  // Para envíos obtenidos de GET /shippings/{id} (endpoint sin migrar, usado por
  // useShippingDetail) quedan undefined/false aquí: ShippingDetail.jsx los
  // completa a posteriori fusionando el resultado de useFullTracking (que sí
  // llama al endpoint unificado, filtrando por shipping_code).
  const trackingInfo = parseTrackingInfo(shipping)
  const isUnscheduledPickup = Boolean(shipping.is_unscheduled)

  // Algunos items del endpoint unificado (envíos multi-bulto) no informan
  // `shipping_status_code` y solo traen `current_status_code` -mismo caso que
  // ya contempla `isCancelled` más abajo-, así que se usa como fallback para
  // no perder el estado (y con él, p.ej. el icono de recogida sin programar).
  const currentStatus = getStatus(
    toWebStatusCode(shipping.shipping_status_code ?? shipping.current_status_code),
  )

  // El endpoint unificado (usado en el listado de seguimiento) no siempre
  // informa `is_nulled` (ver TODO CRÍTICO en transformFiltersToRpcShippingsBody);
  // GET /shippings/{id} (usado en el detalle) sí lo trae. Se cae a comprobar
  // el código de estado anulado directamente para no perder el resaltado en
  // rojo en el listado.
  const isCancelled =
    Boolean(shipping.is_nulled) ||
    [shipping.shipping_status_code, shipping.current_status_code].includes(
      SHIPPING_STATUS_CODES.cancelled,
    )

  // El código del último evento real (ver getLastEventCode) es más fiable que
  // currentStatus.code (basado en shipping_status_code/current_status_code,
  // que puede quedar desactualizado): se usa tanto para la severidad del chip
  // como para `lastStatusCode`, que StatusCodeChip también usa para decidir si
  // pinta el icono de aviso (triángulo).
  const lastEventStatusCode = getLastEventCode(shipping.events) ?? currentStatus.code

  const statusSeverityColor = getColorChipForTrackingState({
    isCancelled,
    isIncident: Boolean(trackingInfo?.isIncident),
    isResolved: trackingInfo?.isResolved,
    canResolve: trackingInfo?.canResolve,
    isUnscheduledPickup,
    isDelivered: currentStatus.code === SHIPPING_STATUS_CODES.delivered,
    statusCode: lastEventStatusCode,
  })
  //OK
  const getOriginalShippingCode = shipping =>
    shipping.related_services?.find(
      service =>
        (service.relation_type === 'original_shipping' &&
          service.relation_reason === 'return') ||
        (service.relation_type === 'original_shipping' &&
          service.relation_reason === 'reverse_logistics') ||
        (service.relation_type === 'original_shipping' &&
          service.relation_reason === 'devolution')
    )?.shipping_code

  const getReturnShippingCode = shipping =>
    shipping.related_services?.find(
      service => service.relation_type === 'devolution_shipping' && service.relation_reason === 'devolution',
    )?.shipping_code

  const getReverseReturnShippingCode = shipping => {
    const fromRelatedServices = shipping.related_services?.find(
      service => service.relation_type === 'reverse_logistics_shipping' && service.relation_reason === 'reverse_logistics',
    )?.shipping_code

    if (fromRelatedServices) return fromRelatedServices

    // El histórico de eventos puede venir embebido en `events` (endpoint
    // unificado) o en `shipping_history.events` (GET /shippings/{id}).
    const web2500Event = (
      shipping.events ?? shipping.shipping_history?.events
    )?.find(event => event.code === 'WEB2500')

    return web2500Event?.detail?.return_shipment_code &&
      web2500Event.detail.return_shipment_code !== 'null'
      ? web2500Event.detail.return_shipment_code.slice(0, -3)
      : undefined
  }

  const getReturnOnDeliveryShippingCode = shipping =>
    shipping.related_services?.find(
      service => service.relation_type === 'return_shipping' && service.relation_reason === 'return',
    )?.shipping_code

  // Parche de texto conservado tal cual (ver plan): no confirmado si el backend
  // ya aplica este reemplazo en tracking_info.incident_description.
  // TODO: validar contra test si sigue haciendo falta.
  const parseIncidentDescription = () => {
    if (
      trackingInfo?.isIncident === '2_INCT' &&
      (currentStatus.name === 'Recogido' || currentStatus.name === 'Grabado')
    ) {
      return capitalize(
        trackingInfo?.incidentDescription?.replace('DESTINATARIO', 'CLIENTE'),
      )
    }
    return capitalize(trackingInfo?.incidentDescription)
  }

  // TODO: validar contra test - la respuesta del endpoint unificado anida los
  // datos de contacto en `sender_contact_info`/`recipient_contact_info` (en vez
  // de los campos planos `sender_*`/`destin_*`/`recipient_*` del `rpc-shippings`
  // viejo). Se mantiene el fallback a los campos planos para que esta misma
  // función siga sirviendo también para GET /shippings/{id} (sin migrar).
  const senderContactInfo = shipping.sender_contact_info
  const recipientContactInfo = shipping.recipient_contact_info
  const normalizedAdditionals = (shipping.additionals ?? []).map(
    normalizeAdditional,
  )

  return {
    info: {
      additionals: normalizedAdditionals,
      previousShippingCode: shipping.previous_shipping_code,
      relatedServices: shipping.related_services,
      shippingCode: shipping.shipping_code,
      isCancelled,
      cancellationDatetime:
        shipping.nulled_datetime === '' ||
          shipping.nulled_datetime === null ||
          shipping.nulled_datetime === undefined
          ? undefined
          : new Date(shipping.nulled_datetime),
      shippingStatusCode: {
        ...currentStatus,
        severity: statusSeverityColor,
      },
      // Objeto con la forma que consume StatusCodeChip (state prop). Antes lo
      // rellenaba el último item de `useShippingStates`; ahora se construye
      // directamente desde tracking_info del propio envío.
      lastShippingStatusCode: trackingInfo
        ? {
          shippingCode: shipping.shipping_code,
          clientCenterCode: shipping.client_center_code,
          lastStatusCode: lastEventStatusCode,
          lastStatusDescription: trackingInfo.text ?? currentStatus.name,
          incidentCode: trackingInfo.isIncident,
          incidentDescription: trackingInfo.incidentDescription,
          isResolved: trackingInfo.isResolved,
          canResolve: trackingInfo.canResolve,
          // TODO: validar contra test si el endpoint unificado expone una fecha
          // de "nueva entrega" tras incidencia (antes venía de
          // new_delivery_date en la respuesta batch de item-history-api).
          newDelivery: shipping.new_delivery_date
            ? format(new Date(shipping.new_delivery_date), 'dd/MM/yyyy')
            : null,
          severity: statusSeverityColor,
        }
        : undefined,
      trackingInfo,
      isUnscheduled: isUnscheduledPickup,
      clientCenterCode: shipping.client_center_code,
      committedDeliveryDatetime:
        shipping.committed_delivery_datetime === '' ||
          shipping.committed_delivery_datetime === null ||
          shipping.committed_delivery_datetime === undefined
          ? undefined
          : format(
            new Date(shipping.committed_delivery_datetime),
            'dd/MM/yyyy',
          ),
      committedDeliveryDatetimeFull:
        shipping.committed_delivery_datetime === '' ||
          shipping.committed_delivery_datetime === null ||
          shipping.committed_delivery_datetime === undefined
          ? undefined
          : format(
            new Date(shipping.committed_delivery_datetime),
            'dd/MM/yyyy HH:mm',
          ),
      shippingTypeCode: shipping.shipping_type_code ?? '',
      // Expuestos solo para poder duplicar un envío (ver duplicateShipping.js)
      // sin pasar por el asistente de Nuevo Envío, que es donde se pedían
      // hasta ahora. rpc-shippings los devuelve tal cual se enviaron al crear
      // el envío original, incluida la declaración de aduana si la tuvo.
      commodityTypeCode: shipping?.commodity_type_code,
      customsCostTypeCode: shipping?.customs_cost_type_code,
      exportInfo: shipping?.export_info,
      // TODO: validar contra test - el ejemplo documentado por backend usaba
      // 'client_references' (plural/array), pero en producción llega
      // 'client_reference' (singular/string, p.ej. 'ref:avisados_eligible_qa').
      // Se soportan ambos formatos.
      clientReference:
        shipping.client_reference ?? shipping.client_references?.[0] ?? '',
      clientReference2:
        shipping.client_references?.length > 1
          ? shipping.client_references[1]
          : '',
      itemCount: shipping.item_count,
      recipientAddress:
        recipientContactInfo?.address ?? shipping.recipient_address,
      destinCountryCode:
        recipientContactInfo?.country_code ?? shipping.destin_country_code,
      destinPostalCode:
        recipientContactInfo?.postal_code ?? shipping.destin_postal_code,
      destinAddress: recipientContactInfo?.town_name ?? shipping.destin_address,
      destinAddress2:
        recipientContactInfo?.address2 ?? shipping.destin_town_name,
      destinName: recipientContactInfo?.name ?? shipping.recipient_name,
      recipientEmail:
        recipientContactInfo?.email_notify_address ??
        shipping.recipient_email_notify_address ??
        '',
      recipientIdentificationNumber:
        shipping.recipient_identification_number ?? '',
      recipientPhone:
        recipientContactInfo?.phones?.[0] ??
        (shipping.recipient_phones ? shipping.recipient_phones[0] : ''),
      recipientName: recipientContactInfo?.name ?? shipping.recipient_name,
      recipientPostalCode:
        recipientContactInfo?.postal_code ?? shipping.recipient_postal_code,
      senderPostalCode:
        senderContactInfo?.postal_code ?? shipping.sender_postal_code,
      recipientAddress2:
        recipientContactInfo?.address2 ?? shipping.recipient_address_2,
      refundValue:
        findAdditional(normalizedAdditionals, SHIPPING_WITH_REFUND_CODE)
          ?.additional_value ?? '',
      senderAddress: senderContactInfo?.town_name ?? shipping.sender_address,
      originCountryCode:
        senderContactInfo?.country_code ?? shipping.origin_country_code,
      recipentEmail:
        recipientContactInfo?.email_notify_address ??
        shipping.recipient_email_notify_address ??
        '',
      recipentPhone: recipientContactInfo?.phones ?? shipping.recipient_phones ?? '',
      senderPhone:
        senderContactInfo?.phones?.[0] ??
        (shipping.sender_phones ? shipping.sender_phones[0] : ''),
      senderName: senderContactInfo?.name ?? shipping.sender_name,
      senderEmail:
        senderContactInfo?.email_notify_address ??
        shipping.sender_email_notify_address ??
        '',
      originPostalCode:
        senderContactInfo?.postal_code ?? shipping.origin_postal_code,
      senderAddress2: senderContactInfo?.address2 ?? shipping.sender_address_2,
      senderStreetAddress: senderContactInfo?.address ?? '',
      shippingItems: shipping.items,
      shippingComments: shipping.pickup?.comments ?? '',
      bestShippingTypeCode: shipping?.best_shipping_type_code,
      cttPrimeShippingCode: shipping?.prime_shipping_code,
      // El endpoint unificado informa el peso en `declared_weight`; se
      // mantiene el fallback a `shipping_weight_declared` (GET /shippings/{id},
      // endpoint sin migrar) para no perder el dato en ese caso.
      shippingWeight: shipping.declared_weight ?? shipping.shipping_weight_declared,
      declaredDeliveryDatetime: shipping.pickup?.date ?? '',
      shippingDate: shipping.shipping_date
        ? format(new Date(shipping.shipping_date), 'dd/MM/yyyy')
        : '',
      manifestDate: shipping.manifest_datetime,
      conversionComments: shipping.delivery?.comments ?? '',
      services: normalizedAdditionals,
      shippingStatusDatetime: shipping.shipping_status_datetime
        ? formatDayAndHour(shipping.shipping_status_datetime)
        : '',
      incident: {
        description: parseIncidentDescription(),
        code: trackingInfo?.isIncident ?? false,
        severity: statusSeverityColor,
      },
      manifestedDate: shipping.manifest_datetime
        ? format(new Date(shipping.manifest_datetime), 'dd/MM/yyyy')
        : '',
      deliveryDate: shipping.pickup?.date
        ? format(new Date(shipping.pickup.date), 'dd/MM/yyyy')
        : null,
      relatedReturn: {
        originalShippingCode: getOriginalShippingCode(shipping),
        returnShippingCode: getReturnShippingCode(shipping),
        reverseReturnShippingCode: getReverseReturnShippingCode(shipping),
        returnOnDeliveryShippingCode: getReturnOnDeliveryShippingCode(shipping),
      },
      pickupUnscheduled: isUnscheduledPickup,
      delivery: shipping?.delivery,
    },
    details: {
      severity: '',
    },
    // El endpoint unificado ya trae `events`/`tracking_info`/`is_unscheduled`
    // embebidos en cada item del listado (los mismos campos que useFullTracking
    // pide aparte filtrando por shipping_code), así que se reutilizan aquí para
    // no repetir la llamada al desplegar el seguimiento de un envío en la lista.
    fullTrackingData: parseFullTrackingResponse(shipping),
  }
}

const formatShippingsData = (shippingsData, statusList) =>
  shippingsData?.map(datum => parseShipping(datum, statusList))

// Completa un envío ya parseado por parseShipping/parseShippingDetailResponse
// con el trackingInfo/isUnscheduled obtenidos por separado desde el endpoint
// unificado (vía useFullTracking, filtrando por shipping_code). Ver
// ShippingDetail.jsx. Desde que useShippingDetail también usa el endpoint
// unificado, `shipping` ya trae trackingInfo y esto es un no-op; se mantiene
// por si algún consumidor futuro pasa un shipping sin ese campo.
const mergeTrackingInfoIntoShipping = (shipping, { trackingInfo, isUnscheduled } = {}) => {
  if (!shipping || !trackingInfo || shipping.info.trackingInfo) return shipping

  const { info } = shipping
  const statusSeverityColor = getColorChipForTrackingState({
    isCancelled: info.isCancelled,
    isIncident: Boolean(trackingInfo.isIncident),
    isResolved: trackingInfo.isResolved,
    canResolve: trackingInfo.canResolve,
    isUnscheduledPickup: Boolean(isUnscheduled),
    isDelivered: info.shippingStatusCode?.code === SHIPPING_STATUS_CODES.delivered,
    statusCode: info.shippingStatusCode?.code,
  })

  return {
    ...shipping,
    info: {
      ...info,
      trackingInfo,
      isUnscheduled: Boolean(isUnscheduled),
      pickupUnscheduled: Boolean(isUnscheduled),
      shippingStatusCode: {
        ...info.shippingStatusCode,
        severity: statusSeverityColor,
      },
      lastShippingStatusCode: {
        shippingCode: info.shippingCode,
        clientCenterCode: info.clientCenterCode,
        lastStatusCode: info.shippingStatusCode?.code,
        lastStatusDescription: trackingInfo.text ?? info.shippingStatusCode?.name,
        incidentCode: trackingInfo.isIncident,
        incidentDescription: trackingInfo.incidentDescription,
        isResolved: trackingInfo.isResolved,
        canResolve: trackingInfo.canResolve,
        newDelivery: info.lastShippingStatusCode?.newDelivery ?? null,
        severity: statusSeverityColor,
      },
      incident: {
        description: trackingInfo.incidentDescription,
        code: trackingInfo.isIncident || false,
        severity: statusSeverityColor,
      },
    },
  }
}

const parseToShippingDetail = shipping => {
  const { info } = shipping

  const shippingWithRefund = findAdditional(
    info.services,
    SHIPPING_WITH_REFUND_CODE,
  )
  const shippingWithInsuranceMerchandise = findAdditional(
    info.services,
    SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
  )

  const shippingWithReturn = findAdditional(info.services, SHIPPING_WITH_RETURN)

  const shippingWithScanClientSlip = findAdditional(
    info.services,
    SHIPPING_WITH_SLIP_SCAN,
  )

  const shippingWithERE = findAdditional(info.services, SHIPPING_WITH_ERE)

  // El adicional de entrega en punto (PUDO destino) informa el nombre del
  // punto en `point_name` sobre el mismo adicional que trae `additional_sub_code`
  // EIP (entrega individual/locker) o EDP (entrega en punto atendido).
  const deliveryPointAdditional = info.services.find(
    additional =>
      ['EIP', 'EDP'].includes(additional.additional_sub_code) &&
      Boolean(additional.point_name),
  )

  const shippingServices = []

  if (!!shippingWithRefund) {
    shippingServices.push({
      title: 'noun:refund-amount',
      value: shippingWithRefund.additional_value,
      extraInfo: 'field:money-suffix',
    })
  }

  if (!!shippingWithInsuranceMerchandise) {
    shippingServices.push({
      title: 'noun:insurance-merchandise',
      value: shippingWithInsuranceMerchandise.additional_value,
      extraInfo: 'field:money-suffix',
    })
  }

  if (!!shippingWithReturn) {
    shippingServices.push({
      title: 'service:with-return',
    })
  }

  if (!!shippingWithScanClientSlip) {
    shippingServices.push({
      title: 'service:scan-client-slip',
    })
  }

  if (!!shippingWithERE) {
    shippingServices.push({
      title: 'service:rdp-send',
      code: 'ERE',
    })
  }
  return {
    from: {
      client: {
        name: 'Centro Cero',
        code: info.clientCenterCode,
      },
      addressState: 'CLIENT',
      name: info.senderName,
      address: info.senderStreetAddress || info.senderAddress2,
      country: info.originCountryCode,
      postalCode: {
        postalCode: info.originPostalCode,
        city: info.senderAddress,
      },
      contactPerson: info.senderName,
      phone: info.senderPhone,
      email: info.senderEmail,
      pickUpDate: info.declaredDeliveryDatetime,
      pickUpTimeSlot: 'esto no sale',
      comments: info.shippingComments,
    },
    to: {
      isNewDirection: true,
      name: info.destinName,
      address: info.recipientAddress || info.destinAddress2,
      country: info.destinCountryCode,
      postalCode: {
        postalCode: info.destinPostalCode,
        city: info.destinAddress,
      },
      contactPerson: info.recipientName,

      phone: info.recipientPhone,
      email: info.recipientEmail,
      department: '',
      comments: info.conversionComments,
      pudoName: info?.delivery?.contact_name ?? deliveryPointAdditional?.point_name,
    },
    shipment: {
      serviceType: info.shippingTypeCode,
      referenceNumber: info.clientReference,
      packages: info.shippingItems,
    },
    services: shippingServices,
    info,
    // shipping.fullTrackingData ya viene calculado por parseShipping (mismos
    // events que useFullTracking pedía aparte filtrando por shipping_code):
    // se reexpone aquí para que ShippingDetail.jsx no tenga que repetir esa
    // llamada contra el mismo endpoint unificado.
    fullTrackingData: shipping.fullTrackingData,
    //TODO REFACTOR FOR NOT NEEDING EXTRA PARSE IN DETAIL
  }
}

// `data` puede venir undefined cuando useShippingDetail (endpoint unificado
// filtrando por shipping_code) no encuentra ningún item, a diferencia de la
// vieja GET /shippings/{id} que fallaba con un error HTTP en ese caso.
const parseShippingDetailResponse = (data, statusList) =>
  data ? parseToShippingDetail(parseShipping(data, statusList)) : undefined

const parseShippingsTrackingResponse = (shippingsResponse, statusList) => {
  const { data, pagination } = shippingsResponse
  const shippings = {
    shippings: formatShippingsData(data, statusList),
    pagination: {
      rowsPerPage: pagination.page_limit,
      // El endpoint unificado ya es 0-indexado (confirmado: page_offset.current
      // vale 0 para la primera página), a diferencia del rpc-shippings viejo
      // (1-indexado, de ahí el -1 que había aquí antes).
      page: pagination.page_offset.current,
      totalRows: pagination.record_count,
    },
  }

  return shippings
}

const mapShippingStatusCode = code => {
  if (code === 'WEB3904') return 'WEB0000'
  if (code === 'WEB0300') return 'WEB0000'

  if (code === 'WEB0400') return 'WEB0500'
  if (code === 'WEB0600') return 'WEB0500'

  if (code === 'WEB0900') return 'WEB1000'
  if (code === 'WEB1100') return 'WEB1000'
  if (code === 'WEB1200') return 'WEB1000'


  if (code === 'WEB2200') return 'WEB1500'
  if (code === 'WEB2300') return 'WEB1500'
  if (code === 'WEB2400') return 'WEB1500'
  if (code === 'WEB2700') return 'WEB1500'
  if (code === 'WEB2800') return 'WEB1500'

  return code
}

// `shippingItem` es el item devuelto por el endpoint unificado
// (POST .../item-history-api/private/v2/rpc-shippings) filtrado por
// shipping_code, con su array `events` embebido — sustituye a la llamada vieja
// GET .../history/{shippingCode}, cuyos eventos venían anidados en
// `data.shipping_history.events`.
// TODO: validar contra test - el ejemplo de evento dado no incluye un campo
// `type` (a diferencia de la respuesta batch vieja, que sí traía
// `type: 'STATUS'` por evento). Si el nuevo formato no lo expone, cualquier
// lógica que filtre por `type === 'STATUS'` deja de funcionar (ver useTrackingLine.js).
const parseFullTrackingResponse = shippingItem => {
  const events = (shippingItem?.events ?? [])
    .slice()
    .sort((a, b) =>
      isBefore(new Date(a.event_date), new Date(b.event_date)) ? -1 : 1,
    )
    .map(step => ({
      committedDeliveryDatetime:
        step.committed_delivery_datetime === ''
          ? undefined
          : step.committed_delivery_datetime,
      itemCode: step.item_code,
      dateTime: format(new Date(step.event_date), 'dd/MM/yyyy | HH:mm'),
      eventName: step.description,
      shippingStatusCode: mapShippingStatusCode(step.code),
      incidentCode: step.detail?.incident_type_code,
      incidentDescription: step.detail?.incident_type_name ?? step.detail?.incident_type_desc,
      userType: step.detail?.user_type,
      deliveryLocation: step.detail?.delivery_location,
      type: step.type,
      latitude: step?.detail?.event_latitude_gps && step.detail.event_latitude_gps !== 'null'
        ? parseFloat(step.detail.event_latitude_gps)
        : null,
      longitude: step?.detail?.event_longitude_gps && step.detail.event_longitude_gps !== 'null'
        ? parseFloat(step.detail.event_longitude_gps)
        : null,
    }))

  // Se exponen trackingInfo/isUnscheduled como propiedades adicionales sobre el
  // propio array de eventos (que sigue siendo un array normal: .map/.filter/
  // .slice funcionan igual) en vez de cambiar la forma de retorno a un objeto,
  // para no romper a los consumidores existentes que tratan el resultado de
  // useFullTracking como un array plano de eventos (TrackingTable, trackingStatus,
  // ActivateReturn). ShippingDetail.jsx sí lee estas dos propiedades extra para
  // completar shippingDetail.info, que viene de GET /shippings/{id} (sin migrar).
  return Object.assign(events, {
    trackingInfo: parseTrackingInfo(shippingItem),
    isUnscheduled: Boolean(shippingItem?.is_unscheduled),
  })
}

const parseShippingSearchResponse = (shippingResponse, statusList) => {
  if (!Boolean(shippingResponse)) {
    return []
  }
  let shippingsResp = shippingResponse
  if (!Array.isArray(shippingsResp)) shippingsResp = [shippingsResp]
  return formatShippingsData(shippingsResp, statusList)
}

export {
  findAdditional,
  getShippingState,
  transformFiltersToRpcShippingsBody,
  transformFiltersToBodyFile,
  parseShippingsTrackingResponse,
  parseFullTrackingResponse,
  parseShippingSearchResponse,
  parseShipping,
  parseShippingDetailResponse,
  mergeTrackingInfoIntoShipping,
}
